import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const getToken = () => localStorage.getItem("token");

export const searchParamsToURLParams = (params) => {
  const urlParams = new URLSearchParams();

  Object.keys(params).forEach((key) => {
    if (params[key] && params[key].toString().trim() !== "") {
      urlParams.append(key, params[key]);
    }
  });

  return urlParams.toString();
};

export const urlParamsToSearchParams = (searchString) => {
  const params = new URLSearchParams(searchString);
  const searchParams = {
    pkEnquiryDetailId: "",
    name: "",
    emailAdd: "",
    phoneNo: "",
    isBNIMember: "",
    chapterName: "",
    interestedIn: "",
    fromDate: "",
    toDate: "",
    bookingDateFrom: "",
    bookingDateTo: "",
    createdDateFrom: "",
    createdDateTo: "",
    pageSize: "10",
    pageNo: "1",
  };

  params.forEach((value, key) => {
    if (searchParams.hasOwnProperty(key)) {
      searchParams[key] = value;
    }
  });

  return searchParams;
};

export const searchEnquiries = createAsyncThunk(
  "enquiry/searchEnquiries",
  async (searchParams, { rejectWithValue }) => {
    try {
      const token = getToken();

      const urlParams = searchParamsToURLParams(searchParams);
      window.history.pushState(
        {},
        "",
        `${window.location.pathname}?${urlParams}`,
      );

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/enquiry/search`,
        searchParams,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateEnquiry = createAsyncThunk(
  "enquiry/updateEnquiry",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const token = getToken();

      const formattedData = {
        ...data,
        isBNIMember: data.isBNIMember?.toString() || "0",
        bookingDate: data.bookingDate ? `${data.bookingDate}T00:00:00` : null,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/enquiry/update/${id}`,
        formattedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      return { id, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const deleteEnquiry = createAsyncThunk(
  "enquiry/deleteEnquiry",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/enquiry/delete/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      return { id, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const exportEnquiries = createAsyncThunk(
  "enquiry/exportEnquiries",
  async (exportParams, { rejectWithValue }) => {
    try {
      const token = getToken();

      const paramsWithLargePageSize = {
        ...exportParams,
        pageSize: "100000",
        pageNo: "1",
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/enquiry/search`,
        paramsWithLargePageSize,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 30000,
        },
      );

      const data = response.data?.enquiryDetails || response.data?.data || [];

      if (data.length === 0) {
        throw new Error("No data to export");
      }

      const worksheetData = data.map((enquiry, index) => ({
        "S.No": index + 1,
        Name: enquiry.name || "",
        Email: enquiry.emailAdd || "",
        Phone: enquiry.phoneNo || "",
        "BNI Member": enquiry.isBNIMember === "1" ? "Yes" : "No",
        "Chapter Name": enquiry.chapterName || "",
        "Interested In": enquiry.interestedIn || "",
        "Booking Date": enquiry.bookingDate
          ? new Date(enquiry.bookingDate).toLocaleDateString("en-IN")
          : "",
        "Created Date": enquiry.createdDate
          ? new Date(enquiry.createdDate).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "",
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(worksheetData);

      const colWidths = [
        { wch: 8 },
        { wch: 25 },
        { wch: 30 },
        { wch: 15 },
        { wch: 12 },
        { wch: 20 },
        { wch: 30 },
        { wch: 15 },
        { wch: 25 },
      ];
      worksheet["!cols"] = colWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, "Enquiries");

      const timestamp =
        new Date().toISOString().split("T")[0] +
        "_" +
        new Date().getHours() +
        "-" +
        new Date().getMinutes();
      const filename = `enquiries_${timestamp}.xlsx`;

      XLSX.writeFile(workbook, filename);

      return {
        count: data.length,
        filename,
        message: `Successfully exported ${data.length} records to ${filename}`,
      };
    } catch (error) {
      console.error("Export error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      let errorMessage = "Export failed";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return rejectWithValue(errorMessage);
    }
  },
);

const enquirySlice = createSlice({
  name: "enquiry",
  initialState: {
    enquiries: [],
    loading: false,
    error: null,
    searchLoading: false,
    searchError: null,
    updateLoading: false,
    updateError: null,
    deleteLoading: false,
    deleteError: null,
    totalResults: 0,
    searchParams: {
      pkEnquiryDetailId: "",
      name: "",
      emailAdd: "",
      phoneNo: "",
      isBNIMember: "",
      chapterName: "",
      interestedIn: "",
      fromDate: "",
      toDate: "",
      bookingDateFrom: "",
      bookingDateTo: "",
      createdDateFrom: "",
      createdDateTo: "",
      pageSize: "10",
      pageNo: "1",
    },
    currentEnquiry: null,
    isModalOpen: false,
    modalType: "",
    exportLoading: false,
    exportError: null,
    exportSuccess: false,
  },
  reducers: {
    setSearchParams: (state, action) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    resetSearchParams: (state) => {
      state.searchParams = {
        pkEnquiryDetailId: "",
        name: "",
        emailAdd: "",
        phoneNo: "",
        isBNIMember: "",
        chapterName: "",
        interestedIn: "",
        fromDate: "",
        toDate: "",
        bookingDateFrom: "",
        bookingDateTo: "",
        createdDateFrom: "",
        createdDateTo: "",
        pageSize: "10",
        pageNo: "1",
      };
    },
    setSearchParamsFromURL: (state, action) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    openModal: (state, action) => {
      state.isModalOpen = true;
      state.currentEnquiry = action.payload.enquiry;
      state.modalType = action.payload.type;
    },
    closeModal: (state) => {
      state.isModalOpen = false;
      state.currentEnquiry = null;
      state.modalType = "";
    },
    clearErrors: (state) => {
      state.error = null;
      state.searchError = null;
      state.updateError = null;
      state.deleteError = null;
    },
    clearExportStatus: (state) => {
      state.exportLoading = false;
      state.exportError = null;
      state.exportSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchEnquiries.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchEnquiries.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.enquiries = action.payload.enquiryDetails || [];
        state.totalResults = action.payload.totalResult || 0;
      })
      .addCase(searchEnquiries.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError =
          action.payload?.message || "Failed to fetch enquiries";
      })
      .addCase(updateEnquiry.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updateEnquiry.fulfilled, (state, action) => {
        state.updateLoading = false;
        const updatedEnquiry = action.payload.data;
        const index = state.enquiries.findIndex(
          (enquiry) => enquiry.pkEnquiryDetailId === action.payload.id,
        );
        if (index !== -1) {
          state.enquiries[index] = {
            ...state.enquiries[index],
            ...updatedEnquiry,
            bookingDate:
              updatedEnquiry.bookingDate || state.enquiries[index].bookingDate,
            modifiedDate: new Date().toISOString(),
          };
        }
      })
      .addCase(updateEnquiry.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError =
          action.payload?.message || "Failed to update enquiry";
      })
      .addCase(deleteEnquiry.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteEnquiry.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.enquiries = state.enquiries.filter(
          (enquiry) => enquiry.pkEnquiryDetailId !== action.payload.id,
        );
        state.totalResults = Math.max(0, state.totalResults - 1);
      })
      .addCase(deleteEnquiry.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError =
          action.payload?.message || "Failed to delete enquiry";
      })
      .addCase(exportEnquiries.pending, (state) => {
        state.exportLoading = true;
        state.exportError = null;
        state.exportSuccess = false;
      })
      .addCase(exportEnquiries.fulfilled, (state, action) => {
        state.exportLoading = false;
        state.exportSuccess = true;
        toast.success(
          `Exported ${action.payload.count} records to ${action.payload.filename}`,
          {
            position: "top-right",
            autoClose: 5000,
          },
        );
      })
      .addCase(exportEnquiries.rejected, (state, action) => {
        state.exportLoading = false;
        state.exportError = action.payload || "Failed to export enquiries";
        toast.error(`Export failed: ${state.exportError}`, {
          position: "top-right",
          autoClose: 5000,
        });
      });
  },
});

export const {
  setSearchParams,
  resetSearchParams,
  setSearchParamsFromURL,
  openModal,
  closeModal,
  clearErrors,
  clearExportStatus,
} = enquirySlice.actions;

export default enquirySlice.reducer;
